import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import BenchmarkingDashboard from "./BenchmarkingDashboard";
import "@/App.css";

// Import Inter font instead of Space Grotesk
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Asset URLs - these can be overridden with environment variables
const LOGO_URL = process.env.REACT_APP_LOGO_URL || "https://customer-assets.emergentagent.com/job_black-mass-db/artifacts/ho6f3cpj_BlackMassMetrics%20Trademark.jpg";
const BACKGROUND_IMAGE_URL = process.env.REACT_APP_BACKGROUND_IMAGE_URL || "https://customer-assets.emergentagent.com/job_black-mass-db/artifacts/kckhzr60_black%20mass%20metrics.jpg";

// Auth Context
const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      fetchUserInfo();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUserInfo = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
      return false;
    }
  };

  const register = async (email, password, full_name, role = 'general_user') => {
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email, password, full_name, role
      });
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, token }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Components
const Footer = () => {
  return (
    <footer className="relative z-20 bg-gray-900 !text-white py-12 mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="max-w-4xl mx-auto">
            <p className="leading-relaxed mb-6 !text-white">
              BlackMassMetrics is a specialist technology venture of Battery Pollution Technologies, created to address critical challenges in the energy transition economy. By delivering rigorous testing, transparent reporting, and benchmark data for recycled battery materials, BlackMassMetrics empowers recyclers, manufacturers, and investors to unlock value while ensuring sustainability and accountability.
            </p>
            <p className="text-base mb-6 !text-white">
              To learn more on our mission visit:{" "}
              <a 
                href="https://www.batterypollution.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white underline hover:text-gray-200"
              >
                www.batterypollution.com
              </a>
            </p>
          </div>
          
          <div className="pt-8 border-t border-gray-700">
            <p className="text-sm text-white">
              © 2024 BlackMassMetrics. All rights reserved. | A venture of Battery Pollution Technologies™
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

const Navigation = () => {
  const { user, logout } = useAuth();

  return (
    <nav className="border-b border-green-600 sticky top-0 z-50 shadow-sm" style={{backgroundColor: 'rgb(20, 140, 0)'}}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center">
            <div className="flex items-center">
              <img 
                src={LOGO_URL}
                alt="BlackMassMetrics Logo"
                className="h-12 w-auto mr-3"
                style={{
                  filter: 'drop-shadow(0px 0px 0px transparent)',
                  mixBlendMode: 'multiply'
                }}
              />
            </div>
          </div>
          
          <div className="flex items-center gap-6">
            {user ? (
              <>
                <span className="text-sm font-medium text-white bg-transparent px-3 py-1 rounded drop-shadow-lg">
                  Powered by Battery Pollution Technologies™
                </span>
                <span className="text-sm text-white bg-white bg-opacity-20 px-3 py-1 rounded-full">
                  {user.full_name} ({user.role.replace('_', ' ')})
                </span>
                <button
                  onClick={logout}
                  data-testid="logout-btn"
                  className="text-sm font-medium px-4 py-2 rounded-lg border border-white border-opacity-30 text-white hover:bg-white hover:bg-opacity-10 transition-colors"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <span className="text-sm font-medium text-white bg-transparent px-3 py-1 rounded drop-shadow-lg">
                Powered by Battery Pollution Technologies™
              </span>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

const AuthForm = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'general_user'
  });
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    let success = false;
    if (isLogin) {
      success = await login(formData.email, formData.password);
    } else {
      success = await register(formData.email, formData.password, formData.full_name, formData.role);
    }
    
    setLoading(false);
    
    if (success) {
      toast.success(isLogin ? 'Logged in successfully!' : 'Account created successfully!');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen relative" 
         style={{
           backgroundImage: `url(${BACKGROUND_IMAGE_URL})`,
           backgroundSize: 'cover',
           backgroundPosition: 'center',
           backgroundRepeat: 'no-repeat',
           backgroundAttachment: 'fixed'
         }}>
      {/* Full page overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-60"></div>
      
      {/* Header Navigation - over the image */}
      <header className="relative z-10 border-b border-green-600 border-opacity-30" style={{backgroundColor: 'rgba(20, 140, 0, 0.9)'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <div className="flex items-center">
                <img 
                  src={LOGO_URL}
                  alt="BlackMassMetrics Logo"
                  className="h-12 w-auto mr-3"
                  style={{
                    filter: 'drop-shadow(0px 0px 0px transparent)',
                    mixBlendMode: 'multiply'
                  }}
                />
              </div>
            </div>
            <div className="flex items-center">
              <span className="text-sm font-medium text-white bg-transparent px-4 py-2 rounded-lg drop-shadow-lg">
                Powered by Battery Pollution Technologies™
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - over the full background image */}
      <div className="relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="relative pb-8 sm:pb-16 md:pb-20 lg:max-w-2xl lg:w-full lg:pb-28 xl:pb-32">
            <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
              <div className="sm:text-left lg:text-left">
                <h1 className="text-4xl tracking-tight font-bold text-white sm:text-5xl md:text-6xl text-left">
                  <span className="block xl:inline">Black mass testing is the cornerstone</span>{' '}
                  <span className="block xl:inline text-white">of sustainable battery recycling.</span>
                </h1>
                <p className="mt-3 text-base text-white sm:mt-5 sm:text-lg sm:max-w-xl md:mt-5 md:text-xl lg:mx-0 text-justify">
                  Our independent laboratory analyses purity, contaminants, and recovery yields across all major chemistries – LCO, NMC, LFP, and LTO. By providing transparent, data-driven reports, we give recyclers, manufacturers, and investors the confidence to optimise processes, reduce waste, and capture maximum material value.
                </p>
                <p className="mt-4 text-base text-white sm:text-lg sm:max-w-xl md:text-xl lg:mx-0 text-justify">
                  Each report ensures accountability in an evolving regulatory environment where traceability and stewardship are essential.
                </p>
                <p className="mt-4 text-base text-white sm:text-lg sm:max-w-xl md:text-xl lg:mx-0 text-justify">
                  With clear, reliable testing outcomes, stakeholders can benchmark performance, secure supply chain trust, and accelerate the shift towards a closed-loop, circular battery economy that is both commercially viable and environmentally responsible.
                </p>
                <div className="mt-5 sm:mt-8 sm:flex lg:justify-start">
                  <div className="rounded-md shadow">
                    <span className="w-full flex items-center justify-center px-8 py-3 border border-white text-base font-medium rounded-md bg-white hover:bg-gray-50 transition-colors cursor-default" style={{color: 'rgb(20, 140, 0)'}}>
                      Comprehensive Black Mass Database
                    </span>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
        
        {/* Login Form positioned where the certification badge was */}
        <div className="lg:absolute lg:top-20 lg:right-8 xl:right-16">
          <div className="max-w-sm w-full bg-white bg-opacity-95 backdrop-blur-sm rounded-lg shadow-lg p-6 border border-white border-opacity-20">
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {isLogin ? 'Sign In' : 'Create Account'}
              </h2>
              <p className="text-sm text-gray-600">
                {isLogin ? 'Access your black mass sample results' : 'Join our professional testing network'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="auth-form">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="email-input"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:border-transparent outline-none transition-colors"
                  style={{'--tw-ring-color': 'rgb(20, 140, 0)'}}
                  placeholder="your@email.com"
                />
              </div>

              {!isLogin && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    data-testid="fullname-input"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:border-transparent outline-none transition-colors"
                    style={{'--tw-ring-color': 'rgb(20, 140, 0)'}}
                    placeholder="John Doe"
                  />
                </div>
              )}

              {!isLogin && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Account Type
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    data-testid="role-select"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:border-transparent outline-none transition-colors"
                    style={{'--tw-ring-color': 'rgb(20, 140, 0)'}}
                  >
                    <option value="general_user">General User</option>
                    <option value="lab_user">Lab/Facility User</option>
                  </select>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="password-input"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:border-transparent outline-none transition-colors"
                  style={{'--tw-ring-color': 'rgb(20, 140, 0)'}}
                  placeholder="••••••••"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="auth-submit-btn"
                className="w-full text-white font-medium py-2 rounded-lg text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
                style={{backgroundColor: 'rgb(20, 140, 0)'}}
              >
                {loading ? 'Processing...' : (isLogin ? 'Sign In' : 'Create Account')}
              </button>
            </form>

            <div className="mt-4 text-center">
              <button
                onClick={() => setIsLogin(!isLogin)}
                data-testid="toggle-auth-btn"
                className="text-xs font-medium hover:opacity-80"
                style={{color: 'rgb(20, 140, 0)'}}
              >
                {isLogin ? "Don't have an account? Create one" : "Already have an account? Sign in"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section - over background */}
      <div className="relative z-10 py-16 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="bg-gray-200 bg-opacity-90 backdrop-blur-sm rounded-lg p-8">
              <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
                Black Mass Testing Core Benefits
              </h2>
              <p className="mt-4 max-w-2xl mx-auto text-xl text-gray-700">
                Independent analysis and transparent reporting to support reliable pricing, trusted recycling outcomes, and a circular battery economy.
              </p>
            </div>
          </div>

          <div className="mt-16">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              <div className="text-center bg-gray-200 bg-opacity-90 backdrop-blur-sm rounded-lg p-6">
                <div className="flex items-center justify-center h-12 w-12 rounded-md mx-auto mb-4" style={{backgroundColor: 'rgb(20, 140, 0)'}}>
                  <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6.938-7.125A9.875 9.875 0 1121.125 12a9.875 9.875 0 01-8.75 9.625 9.875 9.875 0 01-10.125-3L12 12l2.25-2.25" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900">Provenance & Transparency</h3>
                <p className="mt-2 text-base text-gray-700">
                  Every batch sample is tracked and verified, ensuring clear origin records and full confidence in reported results. Transparency builds trust across recyclers, manufacturers, and regulators.
                </p>
              </div>

              <div className="text-center bg-gray-200 bg-opacity-90 backdrop-blur-sm rounded-lg p-6">
                <div className="flex items-center justify-center h-12 w-12 rounded-md mx-auto mb-4" style={{backgroundColor: 'rgb(20, 140, 0)'}}>
                  <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900">Benchmarking Library</h3>
                <p className="mt-2 text-base text-gray-700">
                  A growing database of tested batches enables direct comparison across facilities and chemistries, creating reliable benchmarks that support accurate black mass pricing and market confidence.
                </p>
              </div>

              <div className="text-center bg-gray-200 bg-opacity-90 backdrop-blur-sm rounded-lg p-6">
                <div className="flex items-center justify-center h-12 w-12 rounded-md mx-auto mb-4" style={{backgroundColor: 'rgb(20, 140, 0)'}}>
                  <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900">Detailed Reporting Capability</h3>
                <p className="mt-2 text-base text-gray-700">
                  Comprehensive reporting tools allow stakeholders to access full test results, monitor recycling source quality, and download batch-specific reports for commercial, technical, or compliance use.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

const BatchForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    facility_name: '',
    facility_location: '',
    operator_contact: '',
    date_of_test: '',
    battery_chemistry: 'NMC',
    material_purity: '',
    metal_content: {
      Li: '',
      Co: '',
      Ni: '',
      Mn: '',
      Fe: '',
      Al: '',
      Cu: ''
    },
    moisture_content: '',
    particle_size_distribution: '',
    contaminants: '',
    recovery_yield: '',
    energy_intensity: '',
    waste_stream_notes: '',
    testing_lab_name: '',
    accreditation_certification: ''
  });
  const [loading, setLoading] = useState(false);
  const { token } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        date_of_test: new Date(formData.date_of_test).toISOString(),
        material_purity: parseFloat(formData.material_purity),
        metal_content: {
          Li: parseFloat(formData.metal_content.Li) || 0,
          Co: parseFloat(formData.metal_content.Co) || 0,
          Ni: parseFloat(formData.metal_content.Ni) || 0,
          Mn: parseFloat(formData.metal_content.Mn) || 0,
          Fe: parseFloat(formData.metal_content.Fe) || 0,
          Al: parseFloat(formData.metal_content.Al) || 0,
          Cu: parseFloat(formData.metal_content.Cu) || 0
        },
        moisture_content: parseFloat(formData.moisture_content),
        recovery_yield: parseFloat(formData.recovery_yield),
        energy_intensity: formData.energy_intensity ? parseFloat(formData.energy_intensity) : null
      };

      await axios.post(`${API}/batches`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Batch record created successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create batch record');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('metal_')) {
      const metal = name.split('_')[1];
      setFormData({
        ...formData,
        metal_content: { ...formData.metal_content, [metal]: value }
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-2xl shadow-lg">
      <h2 className="text-2xl font-semibold text-slate-900 mb-8">Create New Batch Record</h2>
      
      <form onSubmit={handleSubmit} className="space-y-8" data-testid="batch-form">
        {/* Facility Information */}
        <div className="bg-slate-50 p-6 rounded-xl">
          <h3 className="text-lg font-medium text-slate-800 mb-4">Facility Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Facility Name</label>
              <input
                type="text"
                name="facility_name"
                value={formData.facility_name}
                onChange={handleChange}
                required
                data-testid="facility-name-input"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Facility Location</label>
              <input
                type="text"
                name="facility_location"
                value={formData.facility_location}
                onChange={handleChange}
                required
                data-testid="facility-location-input"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Operator Contact</label>
              <input
                type="text"
                name="operator_contact"
                value={formData.operator_contact}
                onChange={handleChange}
                required
                data-testid="operator-contact-input"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Date of Test</label>
              <input
                type="date"
                name="date_of_test"
                value={formData.date_of_test}
                onChange={handleChange}
                required
                data-testid="test-date-input"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
            </div>
          </div>
        </div>

        {/* Test Parameters */}
        <div className="bg-slate-50 p-6 rounded-xl">
          <h3 className="text-lg font-medium text-slate-800 mb-4">Test Parameters</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Battery Chemistry</label>
              <select
                name="battery_chemistry"
                value={formData.battery_chemistry}
                onChange={handleChange}
                required
                data-testid="battery-chemistry-select"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              >
                <option value="LCO">LCO</option>
                <option value="NMC">NMC</option>
                <option value="LFP">LFP</option>
                <option value="LTO">LTO</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Material Purity (%)</label>
              <input
                type="number"
                step="0.1"
                name="material_purity"
                value={formData.material_purity}
                onChange={handleChange}
                required
                data-testid="material-purity-input"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Moisture Content (%)</label>
              <input
                type="number"
                step="0.1"
                name="moisture_content"
                value={formData.moisture_content}
                onChange={handleChange}
                required
                data-testid="moisture-content-input"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Recovery Yield (%)</label>
              <input
                type="number"
                step="0.1"
                name="recovery_yield"
                value={formData.recovery_yield}
                onChange={handleChange}
                required
                data-testid="recovery-yield-input"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Energy Intensity (kWh/kg)</label>
              <input
                type="number"
                step="0.1"
                name="energy_intensity"
                value={formData.energy_intensity}
                onChange={handleChange}
                data-testid="energy-intensity-input"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                placeholder="Optional"
              />
            </div>
          </div>
        </div>

        {/* Metal Content */}
        <div className="bg-slate-50 p-6 rounded-xl">
          <h3 className="text-lg font-medium text-slate-800 mb-4">Metal Content (%)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {Object.keys(formData.metal_content).map((metal) => (
              <div key={metal}>
                <label className="block text-sm font-medium text-slate-700 mb-2">{metal}</label>
                <input
                  type="number"
                  step="0.01"
                  name={`metal_${metal}`}
                  value={formData.metal_content[metal]}
                  onChange={handleChange}
                  data-testid={`metal-${metal.toLowerCase()}-input`}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  placeholder="0.00"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Additional Information */}
        <div className="bg-slate-50 p-6 rounded-xl">
          <h3 className="text-lg font-medium text-slate-800 mb-4">Additional Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Particle Size Distribution</label>
              <input
                type="text"
                name="particle_size_distribution"
                value={formData.particle_size_distribution}
                onChange={handleChange}
                required
                data-testid="particle-size-input"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Contaminants</label>
              <input
                type="text"
                name="contaminants"
                value={formData.contaminants}
                onChange={handleChange}
                required
                data-testid="contaminants-input"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Testing Lab Name</label>
              <input
                type="text"
                name="testing_lab_name"
                value={formData.testing_lab_name}
                onChange={handleChange}
                required
                data-testid="lab-name-input"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Accreditation/Certification</label>
              <input
                type="text"
                name="accreditation_certification"
                value={formData.accreditation_certification}
                onChange={handleChange}
                required
                data-testid="accreditation-input"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">Waste Stream Notes</label>
            <textarea
              name="waste_stream_notes"
              value={formData.waste_stream_notes}
              onChange={handleChange}
              required
              rows={3}
              data-testid="waste-notes-textarea"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            data-testid="submit-batch-btn"
            className="px-8 py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-medium rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating...' : 'Create Batch Record'}
          </button>
        </div>
      </form>
    </div>
  );
};

const BatchSearch = ({ onResults, onViewBatch }) => {
  const [filters, setFilters] = useState({
    chemistry: '',
    facility_name: '',
    date_from: '',
    date_to: '',
    purity_min: '',
    contaminant_filter: ''
  });
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState([]);

  useEffect(() => {
    fetchAllBatches();
  }, []);

  const fetchAllBatches = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/batches`);
      setBatches(response.data);
      onResults(response.data);
    } catch (error) {
      toast.error('Failed to fetch batch records');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const searchData = {};
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          if (key === 'date_from' || key === 'date_to') {
            searchData[key] = new Date(filters[key]).toISOString();
          } else if (key === 'purity_min') {
            searchData[key] = parseFloat(filters[key]);
          } else {
            searchData[key] = filters[key];
          }
        }
      });

      const response = await axios.post(`${API}/batches/search`, searchData);
      setBatches(response.data);
      onResults(response.data);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFilters({
      chemistry: '',
      facility_name: '',
      date_from: '',
      date_to: '',
      purity_min: '',
      contaminant_filter: ''
    });
    fetchAllBatches();
  };

  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  return (
    <div className="space-y-8">
      {/* Search Filters - Intertek Style */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Materials Analysis Techniques</h2>
        <p className="text-gray-600 mb-8">Search and filter battery recycling test records using advanced analytical parameters</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3">Battery Chemistry</label>
            <select
              name="chemistry"
              value={filters.chemistry}
              onChange={handleChange}
              data-testid="filter-chemistry-select"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none transition-colors bg-white"
              style={{'--tw-ring-color': 'rgb(20, 140, 0)'}}
            >
              <option value="">All Chemistry Types</option>
              <option value="LCO">LCO - Lithium Cobalt Oxide</option>
              <option value="NMC">NMC - Nickel Manganese Cobalt</option>
              <option value="LFP">LFP - Lithium Iron Phosphate</option>
              <option value="LTO">LTO - Lithium Titanate</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3">Testing Facility</label>
            <input
              type="text"
              name="facility_name"
              value={filters.facility_name}
              onChange={handleChange}
              data-testid="filter-facility-input"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none transition-colors"
              style={{'--tw-ring-color': 'rgb(20, 140, 0)'}}
              placeholder="Search facility name..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3">Minimum Purity (%)</label>
            <input
              type="number"
              step="0.1"
              name="purity_min"
              value={filters.purity_min}
              onChange={handleChange}
              data-testid="filter-purity-input"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none transition-colors"
              style={{'--tw-ring-color': 'rgb(20, 140, 0)'}}
              placeholder="e.g., 90.0"
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3">Test Date From</label>
            <input
              type="date"
              name="date_from"
              value={filters.date_from}
              onChange={handleChange}
              data-testid="filter-date-from-input"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none transition-colors"
              style={{'--tw-ring-color': 'rgb(20, 140, 0)'}}
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3">Test Date To</label>
            <input
              type="date"
              name="date_to"
              value={filters.date_to}
              onChange={handleChange}
              data-testid="filter-date-to-input"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none transition-colors"
              style={{'--tw-ring-color': 'rgb(20, 140, 0)'}}
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-3">Contaminant Analysis</label>
            <input
              type="text"
              name="contaminant_filter"
              value={filters.contaminant_filter}
              onChange={handleChange}
              data-testid="filter-contaminant-input"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent outline-none transition-colors"
              style={{'--tw-ring-color': 'rgb(20, 140, 0)'}}
              placeholder="e.g., plastic, metal..."
            />
          </div>
        </div>
        
        <div className="flex gap-4">
          <button
            onClick={handleSearch}
            disabled={loading}
            data-testid="search-batches-btn"
            className="px-8 py-3 text-white font-medium rounded-lg transition-all duration-200 disabled:opacity-50 hover:opacity-90"
            style={{backgroundColor: 'rgb(20, 140, 0)'}}
          >
            {loading ? 'Searching...' : 'Search Records'}
          </button>
          <button
            onClick={handleClear}
            data-testid="clear-filters-btn"
            className="px-8 py-3 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg transition-colors"
          >
            Clear All Filters
          </button>
        </div>
      </div>

      {/* Search Results */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-8 py-6 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">
            Analysis Results ({batches.length} records found)
          </h3>
          <p className="text-gray-600 mt-1">Comprehensive battery recycling material analysis data</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="batch-results-table">
            <thead style={{backgroundColor: 'rgb(248, 250, 252)'}}>
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Batch Identification</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Testing Facility</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Chemistry Type</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Test Date</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Material Purity</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Analysis</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {batches.map((batch, index) => (
                <tr key={batch.id} className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {batch.batch_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {batch.facility_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    <span className="inline-flex px-3 py-1 text-xs font-semibold text-white rounded-full" style={{backgroundColor: 'rgb(20, 200, 10)'}}>
                      {batch.battery_chemistry}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {new Date(batch.date_of_test).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{color: 'rgb(20, 200, 10)'}}>
                    {batch.material_purity}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => onViewBatch(batch)}
                      data-testid={`view-batch-${batch.batch_id}-btn`}
                      className="font-medium hover:opacity-80 transition-opacity"
                      style={{color: 'rgb(20, 140, 0)'}}
                    >
                      View Analysis Report
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {batches.length === 0 && !loading && (
          <div className="p-16 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Analysis Records Found</h3>
            <p className="text-gray-500">No batch records match your search criteria. Try adjusting your filters or search terms.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const BatchDetails = ({ batch, onClose }) => {
  const [downloading, setDownloading] = useState(false);
  const [paymentSession, setPaymentSession] = useState(null);
  const { user } = useAuth();

  const handleDownloadReport = async () => {
    if (user?.role === 'general_user') {
      // General user needs to pay
      setDownloading(true);
      try {
        const response = await axios.post(`${API}/payments/checkout`, {
          package_type: 'report'
        });
        window.location.href = response.data.url;
      } catch (error) {
        toast.error('Failed to initiate payment');
      } finally {
        setDownloading(false);
      }
    } else {
      // Admin or lab user can download directly
      setDownloading(true);
      try {
        const response = await axios.get(`${API}/reports/pdf/${batch.batch_id}`, {
          responseType: 'blob'
        });
        
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${batch.facility_name}_${batch.batch_id}_${batch.battery_chemistry}_${new Date(batch.date_of_test).toISOString().split('T')[0]}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        toast.success('PDF report downloaded successfully!');
      } catch (error) {
        toast.error('Failed to download report');
      } finally {
        setDownloading(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
          <h2 className="text-2xl font-semibold text-slate-900">Batch Record Details</h2>
          <button
            onClick={onClose}
            data-testid="close-batch-details-btn"
            className="text-slate-400 hover:text-slate-600 text-2xl"
          >
            ×
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Batch Overview */}
          <div className="bg-slate-50 p-6 rounded-xl">
            <h3 className="text-lg font-medium text-slate-800 mb-4">Overview</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-sm font-medium text-slate-600">Batch ID:</span>
                <p className="text-lg font-semibold text-slate-900" data-testid="batch-id-display">{batch.batch_id}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-slate-600">Facility:</span>
                <p className="text-lg text-slate-900">{batch.facility_name}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-slate-600">Location:</span>
                <p className="text-lg text-slate-900">{batch.facility_location}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-slate-600">Test Date:</span>
                <p className="text-lg text-slate-900">{new Date(batch.date_of_test).toLocaleDateString()}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-slate-600">Chemistry:</span>
                <span className="inline-block px-3 py-1 text-sm font-medium bg-orange-100 text-orange-800 rounded-full">
                  {batch.battery_chemistry}
                </span>
              </div>
              <div>
                <span className="text-sm font-medium text-slate-600">Material Purity:</span>
                <p className="text-lg font-semibold text-green-600">{batch.material_purity}%</p>
              </div>
            </div>
          </div>

          {/* Metal Content Chart */}
          <div className="bg-slate-50 p-6 rounded-xl">
            <h3 className="text-lg font-medium text-slate-800 mb-4">Metal Content Distribution</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              {Object.entries(batch.metal_content).map(([metal, percentage]) => (
                <div key={metal} className="text-center">
                  <div className="bg-white p-3 rounded-lg border">
                    <div className="text-2xl font-bold text-slate-900">{percentage}%</div>
                    <div className="text-sm font-medium text-slate-600">{metal}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Test Parameters */}
          <div className="bg-slate-50 p-6 rounded-xl">
            <h3 className="text-lg font-medium text-slate-800 mb-4">Test Parameters</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-sm font-medium text-slate-600">Moisture Content:</span>
                <p className="text-lg text-slate-900">{batch.moisture_content}%</p>
              </div>
              <div>
                <span className="text-sm font-medium text-slate-600">Recovery Yield:</span>
                <p className="text-lg text-slate-900">{batch.recovery_yield}%</p>
              </div>
              <div>
                <span className="text-sm font-medium text-slate-600">Energy Intensity:</span>
                <p className="text-lg text-slate-900">{batch.energy_intensity || 'N/A'} kWh/kg</p>
              </div>
              <div>
                <span className="text-sm font-medium text-slate-600">Particle Size:</span>
                <p className="text-lg text-slate-900">{batch.particle_size_distribution}</p>
              </div>
              <div className="md:col-span-2">
                <span className="text-sm font-medium text-slate-600">Contaminants:</span>
                <p className="text-lg text-slate-900">{batch.contaminants}</p>
              </div>
            </div>
          </div>

          {/* Lab Information */}
          <div className="bg-slate-50 p-6 rounded-xl">
            <h3 className="text-lg font-medium text-slate-800 mb-4">Lab Information</h3>
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-slate-600">Testing Lab:</span>
                <p className="text-lg text-slate-900">{batch.testing_lab_name}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-slate-600">Accreditation:</span>
                <p className="text-lg text-slate-900">{batch.accreditation_certification}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-slate-600">Waste Stream Notes:</span>
                <p className="text-lg text-slate-900">{batch.waste_stream_notes}</p>
              </div>
            </div>
          </div>

          {/* Download Section */}
          <div className="bg-gradient-to-r from-orange-50 to-red-50 p-6 rounded-xl border border-orange-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-slate-800">PDF Report</h3>
                <p className="text-sm text-slate-600 mt-1">
                  {user?.role === 'general_user' 
                    ? 'Purchase and download detailed PDF report ($25)'
                    : 'Download detailed PDF report'
                  }
                </p>
              </div>
              <button
                onClick={handleDownloadReport}
                disabled={downloading}
                data-testid="download-report-btn"
                className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-medium rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {downloading ? 'Processing...' : 
                  (user?.role === 'general_user' ? 'Purchase & Download ($25)' : 'Download PDF')
                }
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PaymentSuccess = () => {
  const [checking, setChecking] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      checkPaymentStatus(sessionId);
    } else {
      setChecking(false);
    }
  }, []);

  const checkPaymentStatus = async (sessionId) => {
    let attempts = 0;
    const maxAttempts = 5;
    
    const poll = async () => {
      if (attempts >= maxAttempts) {
        setPaymentStatus({ status: 'timeout' });
        setChecking(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        setPaymentStatus(response.data);
        
        if (response.data.payment_status === 'paid') {
          setChecking(false);
          toast.success('Payment successful! You can now download reports.');
        } else if (response.data.status === 'expired') {
          setChecking(false);
        } else {
          attempts++;
          setTimeout(poll, 2000);
        }
      } catch (error) {
        attempts++;
        if (attempts >= maxAttempts) {
          setPaymentStatus({ status: 'error' });
          setChecking(false);
        } else {
          setTimeout(poll, 2000);
        }
      }
    };
    
    poll();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        {checking ? (
          <div>
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-2">Processing Payment</h2>
            <p className="text-slate-600">Please wait while we verify your payment...</p>
          </div>
        ) : (
          <div>
            {paymentStatus?.payment_status === 'paid' ? (
              <div>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-2xl font-semibold text-slate-900 mb-2">Payment Successful!</h2>
                <p className="text-slate-600 mb-6">You can now access and download PDF reports.</p>
                <button
                  onClick={() => window.location.href = '/'}
                  data-testid="return-home-btn"
                  className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-medium rounded-xl transition-all duration-200"
                >
                  Return to Database
                </button>
              </div>
            ) : (
              <div>
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h2 className="text-2xl font-semibold text-slate-900 mb-2">Payment Issue</h2>
                <p className="text-slate-600 mb-6">
                  {paymentStatus?.status === 'timeout' 
                    ? 'Payment verification timed out. Please check your email for confirmation.' 
                    : 'There was an issue processing your payment. Please try again.'}
                </p>
                <button
                  onClick={() => window.location.href = '/'}
                  data-testid="try-again-btn"
                  className="px-6 py-3 bg-slate-600 hover:bg-slate-700 text-white font-medium rounded-xl transition-colors"
                >
                  Try Again
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('search');
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [searchResults, setSearchResults] = useState([]);

  const canCreateBatch = user?.role === 'admin' || user?.role === 'lab_user';

  const handleBatchCreated = () => {
    setActiveTab('search');
    toast.success('Batch record created successfully!');
  };

  return (
    <div className="min-h-screen" style={{backgroundColor: 'rgb(20, 140, 0)'}}>
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header - Intertek Style */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Materials Analysis and Testing</h1>
          <p className="text-lg text-white opacity-90 max-w-4xl">
            Our independent laboratory analyses purity, contaminants, and recovery yields across all major chemistries – LCO, NMC, LFP, and LTO. By providing transparent, data-driven reports, we give recyclers, manufacturers, and investors the confidence to optimise processes, reduce waste, and capture maximum material value.
          </p>
        </div>

        {/* Services Overview */}
        <div className="mb-8 bg-gray-200 rounded-lg shadow-sm border border-gray-300 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Black Mass Testing Core Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-gray-100 rounded-lg">
              <div className="w-12 h-12 mx-auto mb-3 rounded-lg flex items-center justify-center" style={{backgroundColor: 'rgb(20, 140, 0)'}}>
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6.938-7.125A9.875 9.875 0 1121.125 12a9.875 9.875 0 01-8.75 9.625 9.875 9.875 0 01-10.125-3L12 12l2.25-2.25" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Provenance & Transparency</h3>
              <p className="text-sm text-gray-600">Every batch sample is tracked and verified, ensuring clear origin records and full confidence in reported results.</p>
            </div>
            
            <div className="text-center p-4 bg-gray-100 rounded-lg">
              <div className="w-12 h-12 mx-auto mb-3 rounded-lg flex items-center justify-center" style={{backgroundColor: 'rgb(20, 140, 0)'}}>
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Benchmarking Library</h3>
              <p className="text-sm text-gray-600">A growing database enabling direct comparison across facilities and chemistries for reliable benchmarks.</p>
            </div>
            
            <div className="text-center p-4 bg-gray-100 rounded-lg">
              <div className="w-12 h-12 mx-auto mb-3 rounded-lg flex items-center justify-center" style={{backgroundColor: 'rgb(20, 140, 0)'}}>
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Detailed Reporting Capability</h3>
              <p className="text-sm text-gray-600">Comprehensive reporting tools for full test results and batch-specific reports for commercial use.</p>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <nav className="flex space-x-1 bg-white p-1 rounded-lg shadow-sm border border-gray-200" data-testid="tab-navigation">
            <button
              onClick={() => setActiveTab('search')}
              data-testid="search-tab-btn"
              className={`flex-1 py-3 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'search'
                  ? 'text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
              style={activeTab === 'search' ? {backgroundColor: 'rgb(20, 140, 0)'} : {}}
            >
              Search & Browse Records
            </button>
            <button
              onClick={() => setActiveTab('benchmarking')}
              data-testid="benchmarking-tab-btn"
              className={`flex-1 py-3 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'benchmarking'
                  ? 'text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
              style={activeTab === 'benchmarking' ? {backgroundColor: 'rgb(20, 140, 0)'} : {}}
            >
              Benchmarking Dashboard
            </button>
            {canCreateBatch && (
              <button
                onClick={() => setActiveTab('create')}
                data-testid="create-tab-btn"
                className={`flex-1 py-3 px-4 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'create'
                    ? 'text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
                style={activeTab === 'create' ? {backgroundColor: 'rgb(20, 140, 0)'} : {}}
              >
                Create New Batch Record
              </button>
            )}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'search' && (
            <BatchSearch 
              onResults={setSearchResults} 
              onViewBatch={setSelectedBatch}
            />
          )}
          
          {activeTab === 'benchmarking' && (
            <BenchmarkingDashboard 
              onViewBatch={setSelectedBatch}
            />
          )}
          
          {activeTab === 'create' && canCreateBatch && (
            <BatchForm onSuccess={handleBatchCreated} />
          )}
        </div>
      </div>

      {/* Batch Details Modal */}
      {selectedBatch && (
        <BatchDetails 
          batch={selectedBatch} 
          onClose={() => setSelectedBatch(null)}
        />
      )}
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route 
            path="/payment-success" 
            element={<PaymentSuccess />} 
          />
          <Route 
            path="/payment-cancel" 
            element={
              <div className="min-h-screen bg-slate-50 flex flex-col">
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <h2 className="text-2xl font-semibold text-slate-900 mb-4">Payment Cancelled</h2>
                    <p className="text-slate-600 mb-6">Your payment was cancelled. You can try again anytime.</p>
                    <button
                      onClick={() => window.location.href = '/'}
                      className="px-6 py-3 bg-slate-600 hover:bg-slate-700 text-white font-medium rounded-xl transition-colors"
                    >
                      Return to Database
                    </button>
                  </div>
                </div>
                <Footer />
              </div>
            } 
          />
          <Route 
            path="/" 
            element={user ? <Dashboard /> : <AuthForm />} 
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default function AppWithProvider() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}
